import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import JsdhNotifikaceService from "./JsdhNotifikaceService";

type OdpovedTyp = "JEDU" | "NEJEDU";

interface OdpovedInput {
    poplachId: number;
    token: string;
    odpoved: OdpovedTyp;
}

interface OdpovedContext {
    previousOdpoved: string | null | undefined;
    snapshotTakenAt: number;
}

/**
 * HO-28 H1 (BEZPEČNOSTNĚ-KRITICKÉ):
 *
 * Po kliknutí JEDU/NEJEDU NENÍ povolen optimistic update, který by hasiči ukázal
 * "Již jste odpověděl: JEDU" před tím, než BE odpověď opravdu uloží. Pokud by BE
 * vrátil 403 (invalid/expirovaný token), 4xx/5xx nebo connection timeout, mohlo
 * by zůstat v UI "potvrzeno", aniž by jednotka odpověď reálně dostala —
 * a hasič by nepřijel k poplachu.
 *
 * Pattern:
 *   - onMutate uloží snapshot předchozí hodnoty z cache (rollback target).
 *   - Optimisticky NEPŘEPISUJEME cache `uzivatelovaOdpoved` — UI zobrazí
 *     stav "odesílám" přes `mutation.isPending`, nikoli přes data v cache.
 *   - Při úspěchu invalidujeme query, BE response je single source of truth.
 *   - Při chybě se cache nezmění (žádný optimistic update), takže není co
 *     rollbackovat. Snapshot ponecháváme jako defenzivní pojistku pro případ,
 *     že by někdo v budoucnu znovu přidal optimistic setQueryData.
 *   - Error toast má extended duration + page-level error banner v OdpovedPage
 *     drží stav viditelný, dokud uživatel chybu nezavře a nezkusí znovu.
 */
export const useOdpovedMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, unknown, OdpovedInput, OdpovedContext>({
        mutationFn: ({ poplachId, token, odpoved }) =>
            JsdhNotifikaceService.submitOdpoved(poplachId, token, { odpoved }),

        onMutate: async ({ poplachId, token }) => {
            const key = ["uzivatelovaOdpoved", poplachId, token];

            // Zrušíme probíhající dotazy, aby refetch nemohl přebít náš stav
            await queryClient.cancelQueries({ queryKey: key });

            // Uložíme si snapshot pro případný rollback (defenzivně,
            // pokud by někdo v budoucnu znovu zavedl optimistic update).
            const previousOdpoved = queryClient.getQueryData<string | null>(key);

            // NEPROVÁDÍME optimistic setQueryData — UI musí čekat na BE.

            return { previousOdpoved, snapshotTakenAt: Date.now() };
        },

        onError: (_err, { poplachId, token }, context) => {
            const key = ["uzivatelovaOdpoved", poplachId, token];

            // Defenzivní rollback: pokud cokoli (např. paralelní mutace nebo
            // budoucí změna kódu) změnilo cache, vrátíme snapshot.
            if (context?.previousOdpoved !== undefined) {
                queryClient.setQueryData<string | null>(key, context.previousOdpoved);
            }

            // Zamezíme aktivaci klávesnice
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            showToast(
                "CHYBA: Odpověď NEBYLA odeslána. Zkontrolujte připojení a zkuste znovu.",
                "error"
            );
        },

        onSuccess: () => {
            // Zamezíme aktivaci klávesnice
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            showToast("Odpověď byla zaznamenána.", "success");
        },

        onSettled: (_data, _error, { poplachId, token }) => {
            // Zamezíme aktivaci klávesnice
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            // Vždy si vyžádáme čerstvý stav od BE — to je single source of truth.
            queryClient.invalidateQueries({ queryKey: ["uzivatelovaOdpoved", poplachId, token] });
        },
    });
};
